import React, { useCallback, useEffect } from 'react'

import { AssetsGrid } from 'features/assets/ui/AssetsGrid'
import { BoardToolbarAction, BoardAssetsToolbarAction } from 'shared/constants/toolbar'
import { AddNewBoardModal } from 'features/boards/ui/AddNewBoardModal'
import { PublicBoardHeader } from 'features/public-link-files/ui/PublicBoardHeader'
import { BoardsGrid } from 'features/boards/ui/BoardsGrid'
import { BoardToolbarTop } from 'features/boards/ui/BoardToolbarTop'
import { AdjustableSection } from 'features/common/ui/dashboard/AdjustableSection'
import { useScrollContainer } from 'features/common/ui/dashboard/Layout'
import { ToolbarTop } from 'features/common/ui/dashboard/ToolbarTop'
import { EmptyBoardPlaceholder } from 'features/common/ui/EmptyBoardPlaceholder'
import { MultiSelectTool } from 'features/common/ui/MultiSelectTool'
import { SkeletonBoard } from 'features/common/ui/SkeletonBoard'
import { EMPTY_BOARD_TEXTS, TARGET_CLASS_NAMES } from 'features/public-link-files/constants/publicFiles'
import { usePublicLinkLogic } from 'features/public-link-files/hooks/usePublicLinkLogic'
import { PublicFilesPageProps } from 'features/public-link-files/types/publicFiles'
import { TopSection } from 'features/public-link-files/ui/TopSection'
import { AppstoreAddOutlined, ArrowRightOutlined, ArrowsAltOutlined, CopyOutlined, DownloadOutlined, EditOutlined, MinusCircleOutlined } from 'shared/ui'
import styled from 'styled-components'
import { DropzoneArea } from 'features/dropzone/DropzoneArea'
import { dropzoneActions, removeUploadHandlerThunk, setUploadHandlerThunk } from 'features/dropzone/dropzoneSlice'
import { getIsDropzoneAreaVisibleSelector } from 'features/dropzone/selectors'
import { useAppDispatch } from 'shared/config/store'

import { assetsActions, moveAssetsThunk } from '../../features/assets/model/slice/assetsSlice'
import { boardsActions, moveBoardsThunk } from '../../features/boards/model/slice/boardsSlice'

export const PublicFilesPage = React.memo<PublicFilesPageProps>(({
  isAuthenticatedView = false
}) => {
  const dispatch = useAppDispatch()
  const scrollContainerRef = useScrollContainer()
  const isDropzoneAreaVisible = getIsDropzoneAreaVisibleSelector()
  const {
    subBoards,
    assets,
    selectedAssetsIds,
    selectedBoardsIds,
    selectedBoards,
    selectedAssets,
    breadcrumbs,
    currentBoardData,
    boardDescription,
    nodeFields,
    isAuth,
    isSingleFileLink,
    isMouseSelecting,
    isSubBoardsEmpty,
    isAssetsEmpty,
    isEmptyBoard,
    isPageLoading,
    isEditContentEnabled,
    isUploadEnabled,
    filterActionsByPermissions,
    handleContextMenuStateChange,
    isContextMenuOpen,
    handleMouseSelect,
    handleMouseSelectEnd,
    handleMouseSelectStart,
    handleSelectAsset,
    handleSelectBoard,
    handleBoardContextActionClick,
    handleAssetsContextActionClick,
    handleUploadFiles,
    handleCloseBoardsToolbar,
    handleCloseAssetsToolbar,
    handleCloseMixedToolbar,
    handleBoardDoubleClick,
    handleAssetDoubleClick,
    handleAuthClick,
    handleCreateAccountClick,
    handleToolbarActionClick,
    handleBoardStatusChange,
    handleDescriptionChange,
    handleTitleChange,
    handleChangeBoardTags,
    handleCreateBoardTag,
    boardUIStatus,
    boardUIStatuses,
  } = usePublicLinkLogic({ isAuthenticatedView })
  const isUploadOrEditEnabled = isUploadEnabled || isEditContentEnabled

  // dropzone settings
  useEffect(() => {
    dispatch(setUploadHandlerThunk({ callback: handleUploadFiles }))

    dispatch(dropzoneActions.updateDropzoneOptions({
      disabled: !isUploadOrEditEnabled
    }))

    dispatch(dropzoneActions.toggleDropzoneAreaVisibility(true))

    return () => {
      dispatch(removeUploadHandlerThunk())
      dispatch(dropzoneActions.updateDropzoneOptions({
        disabled: true
      }))
    }
  }, [dispatch, handleUploadFiles, isUploadEnabled])

  const toolbarActionsIcons = {
    [BoardToolbarAction.Open]: <ArrowsAltOutlined />,
    [BoardToolbarAction.Download]: <DownloadOutlined />,
    [BoardToolbarAction.Rename]: <EditOutlined />,
    [BoardToolbarAction.RemoveFromBoard]: <MinusCircleOutlined />,
    [BoardToolbarAction.Move]: <ArrowRightOutlined />,
    [BoardToolbarAction.Copy]: <CopyOutlined />,
    [BoardToolbarAction.AddToAnotherBoard]: <AppstoreAddOutlined />,
  }

  const contextMenuActionsIcons = {
    [BoardToolbarAction.Open]: <ArrowsAltOutlined />,
    [BoardToolbarAction.Download]: <DownloadOutlined />,
    [BoardToolbarAction.Rename]: <EditOutlined />,
    [BoardToolbarAction.RemoveFromBoard]: <MinusCircleOutlined />,
    [BoardToolbarAction.Move]: <ArrowRightOutlined />,
    [BoardToolbarAction.Copy]: <CopyOutlined />,
    [BoardToolbarAction.AddToAnotherBoard]: <AppstoreAddOutlined />,
  }

  const contextMenuItemsAssets = useCallback(() => filterActionsByPermissions([
    { key: BoardToolbarAction.Open, label: 'Open', icon: contextMenuActionsIcons[BoardToolbarAction.Open] },
    { key: BoardToolbarAction.Download, label: 'Download', icon: contextMenuActionsIcons[BoardToolbarAction.Download] },
    { key: BoardToolbarAction.Rename, label: 'Rename', icon: contextMenuActionsIcons[BoardToolbarAction.Rename] },
    { key: BoardToolbarAction.Move, label: 'Move to board', icon: contextMenuActionsIcons[BoardToolbarAction.Move] },
    { key: BoardToolbarAction.AddToAnotherBoard, label: 'Add to board', icon: contextMenuActionsIcons[BoardToolbarAction.AddToAnotherBoard] },
    { key: BoardToolbarAction.RemoveFromBoard, label: 'Remove from board', icon: contextMenuActionsIcons[BoardToolbarAction.RemoveFromBoard] },
  ]), [])

  const contextMenuItemsBoards = filterActionsByPermissions([
    { key: BoardToolbarAction.Open, label: 'Open', icon: contextMenuActionsIcons[BoardToolbarAction.Open] },
    { key: BoardToolbarAction.Download, label: 'Download', icon: contextMenuActionsIcons[BoardToolbarAction.Download] },
    { key: BoardToolbarAction.Rename, label: 'Rename', icon: contextMenuActionsIcons[BoardToolbarAction.Rename] },
    { key: BoardToolbarAction.Move, label: 'Move to board', icon: contextMenuActionsIcons[BoardToolbarAction.Move] },
    { key: BoardToolbarAction.Copy, label: 'Copy to board', icon: contextMenuActionsIcons[BoardToolbarAction.Copy] },

  ])

  const availableBoardsToolbarActions = filterActionsByPermissions([
    { id: BoardToolbarAction.Open, name: "Open" },
    { id: BoardToolbarAction.Download, name: "Download" },
    { id: BoardToolbarAction.Rename, name: "Rename" },

  ])

  const availableExtraBoardsToolbarActionsGroups = [
    filterActionsByPermissions([
      {
        key: BoardAssetsToolbarAction.Move,
        label: "Move to board",
        icon: contextMenuActionsIcons[BoardToolbarAction.Move]
      },
      selectedBoardsIds.length === 1 && !selectedAssetsIds.length && { key: BoardToolbarAction.Copy, label: "Copy to board", icon: contextMenuActionsIcons[BoardToolbarAction.Copy] },
    ])
  ]

  const availableAssetsToolbarActions = filterActionsByPermissions([
    { id: BoardToolbarAction.Open, name: "Open" },
    { id: BoardToolbarAction.Download, name: "Download" },
    { id: BoardToolbarAction.Rename, name: "Rename" },
  ])

  const availableExtraAssetsToolbarActionsGroups = [
    filterActionsByPermissions([
      {
        key: BoardAssetsToolbarAction.Move,
        label: 'Move to board',
        icon: contextMenuActionsIcons[BoardAssetsToolbarAction.Move],
      },
      {
        key: BoardAssetsToolbarAction.AddToAnotherBoard,
        label: "Add to board",
        icon: contextMenuActionsIcons[BoardAssetsToolbarAction.AddToAnotherBoard],
      },
      selectedAssetsIds.length >= 1 && !selectedBoardsIds.length && {
        key: BoardAssetsToolbarAction.RemoveFromBoard,
        label: 'Remove from board',
        icon: contextMenuActionsIcons[BoardAssetsToolbarAction.RemoveFromBoard],
      },
    ])
  ]

  const handleBoardToolbarTopAction = (actionId: BoardToolbarAction) => {
    const boardId = currentBoardData?.node_id
    if (boardId) {
      handleBoardContextActionClick(actionId, boardId)
    }
  }

  const getToolbarTopProps = () => {
    const hasSelectedBoards = selectedBoardsIds.length > 0
    const hasSelectedAssets = selectedAssetsIds.length > 0

    return {
      itemsType: hasSelectedBoards && hasSelectedAssets
        ? "mixed" as const
        : hasSelectedBoards
          ? "board" as const
          : "asset" as const,
      mainActions: hasSelectedBoards
        ? availableBoardsToolbarActions
        : availableAssetsToolbarActions,
      selectedItems: [
        ...(hasSelectedBoards ? selectedBoards : []),
        ...(hasSelectedAssets ? selectedAssets : [])
      ],
      onActionClick: (itemsType, actionId, selectedItems) => handleToolbarActionClick(itemsType as any, actionId as any, selectedItems as any),
      actionsIcons: toolbarActionsIcons,
      extraActionsGroups: hasSelectedBoards
        ? availableExtraBoardsToolbarActionsGroups
        : availableExtraAssetsToolbarActionsGroups,
      onClose: hasSelectedBoards && hasSelectedAssets
        ? handleCloseMixedToolbar
        : hasSelectedBoards
          ? handleCloseBoardsToolbar
          : handleCloseAssetsToolbar,
      multiselectSupportedActions: [
        BoardToolbarAction.Download,
        BoardToolbarAction.RemoveFromBoard,
        BoardToolbarAction.Move,
        BoardToolbarAction.Copy,
      ]
    }
  }

  const handleAssetDropOnBoard = useCallback((assetIds: string[], boardId: string) => {
    if (assetIds.length > 0 && boardId) {
      dispatch(moveAssetsThunk({ ids: assetIds, destinationId: boardId }))
      dispatch(assetsActions.unselectAll())
    }
  }, [dispatch])

  const handleBoardDropOnBoard = useCallback((boardIds: string[], targetBoardId: string) => {
    if (boardIds.length > 0 && targetBoardId) {
      dispatch(moveBoardsThunk({ ids: boardIds, destinationId: targetBoardId }))
      dispatch(boardsActions.unselectAll())
    }
  }, [dispatch])

  const handleBoardDragStart = (boardId: string) => {
    void boardId
  }

  if (isPageLoading) {
    return (
      <SBoardPage $isAuth={isAuth}>
        <SkeletonBoard />
      </SBoardPage>
    )
  }

  const getTagsMode = (): 'select-create' | 'select-only' => {
    return 'select-only'
  }

  if (isSingleFileLink) {
    return null
  }

  return (
    <SBoardPage
      $needHeight={isEmptyBoard && !isPageLoading}
      $isAuth={isAuth}
    >
      <TopSection
        onUpload={handleUploadFiles}
        breadcrumbs={breadcrumbs as any}
        onAuthClick={handleAuthClick}
        onCreateAccountClick={handleCreateAccountClick}
        isAuth={isAuth}
      />

      <BoardToolbarTop
        status={boardUIStatus}
        onClickAction={handleBoardToolbarTopAction}
        onChangeStatus={(statusOption) => {
          handleBoardStatusChange(statusOption)
        }}
        statuses={boardUIStatuses}
        onAddBoard={() => undefined}
        withOnlyStatus={true}
        disabled={!isEditContentEnabled}
      />

      <PublicBoardHeader
        title={currentBoardData?.name || ''}
        description={boardDescription}
        onTitleChange={handleTitleChange}
        onDescriptionChange={handleDescriptionChange}
        onTagsChange={handleChangeBoardTags}
        onCreateTag={handleCreateBoardTag}
        boardTags={nodeFields?.tags?.value || []}
        systemTags={(nodeFields?.tags?.field?.options || [])}
        disableTitle={!isEditContentEnabled}
        disableDescription={!isEditContentEnabled}
        disableTags={!isEditContentEnabled}
        disableStatus={!isEditContentEnabled}
        tagsSelectorMode={getTagsMode()}
      />

      {!isSubBoardsEmpty && (
        <SBoardsSection title="Boards">
          <BoardsGrid
            boards={subBoards}
            selectedItemsIds={selectedBoardsIds}
            selectedAssetsIds={selectedAssetsIds}
            assets={assets}
            onSelect={handleSelectBoard}
            onBoardDoubleClick={handleBoardDoubleClick}
            onMenuItemClick={handleBoardContextActionClick}
            selectedTargetClassName={TARGET_CLASS_NAMES.BOARD_CARD}
            contextMenuItems={contextMenuItemsBoards}
            isItemHoverEnabled={!isMouseSelecting}
            onContextStateChanged={(isOpened) => handleContextMenuStateChange('boards', isOpened)}
            isOuterContextMenuOpened={isContextMenuOpen('assets')}
            onAssetDropOnBoard={handleAssetDropOnBoard}
            onBoardDropOnBoard={handleBoardDropOnBoard}
            onBoardDragStart={handleBoardDragStart}
          />
        </SBoardsSection>
      )}

      {!isAssetsEmpty && (
        <SAssetsSection title="Assets">
          <AssetsGrid
            assets={assets}
            board={currentBoardData}
            onSelect={handleSelectAsset}
            selectedItemsIds={selectedAssetsIds}
            onMenuItemClick={handleAssetsContextActionClick}
            selectedBoardsIds={selectedBoardsIds}
            boards={subBoards}
            selectedTargetClassName={TARGET_CLASS_NAMES.ASSET_CARD}
            contextMenuItems={contextMenuItemsAssets}
            isItemHoverEnabled={!isMouseSelecting}
            onContextStateChanged={(isOpened) => handleContextMenuStateChange('assets', isOpened)}
            isOuterContextMenuOpened={isContextMenuOpen('boards')}
            onDoubleClick={handleAssetDoubleClick}
          />
        </SAssetsSection>
      )}

      {isEmptyBoard && !isPageLoading && (
        <EmptyBoardPlaceholder texts={EMPTY_BOARD_TEXTS} />
      )}

      {(selectedBoardsIds.length > 0 || selectedAssetsIds.length > 0) && (
        <ToolbarTop {...getToolbarTopProps()} />
      )}

      <MultiSelectTool
        onSelect={handleMouseSelect}
        onSelectStart={handleMouseSelectStart}
        onSelectEnd={handleMouseSelectEnd}
        selectedTargetClassNames={[TARGET_CLASS_NAMES.BOARD_CARD, TARGET_CLASS_NAMES.ASSET_CARD]}
        scrollRef={scrollContainerRef}
      />

      <StyledDropzoneArea
        visible={isDropzoneAreaVisible}
        text={'The files will be uploaded to the board'}
      />

      <AddNewBoardModal />
    </SBoardPage>
  )
})

const SBoardPage = styled.div<{ $needHeight?: boolean; $isAuth?: boolean }>`
  padding: ${({ $isAuth }) => $isAuth ? '0' : '24px'};
  position: relative;
  height: ${({ $needHeight }) => $needHeight ? '100%' : 'unset'};
`

const SBoardsSection = styled(AdjustableSection)`
  margin-top: 48px;
`

const SAssetsSection = styled(AdjustableSection)`
  margin-top: 48px;
`

const StyledDropzoneArea = styled(DropzoneArea)`
  position: fixed;
  top: 0;
  left: 0;
  width: 100vw;
  height: 100vh;
  z-index: 1000;
  pointer-events: none;
`
